/**
 * DesignerNodeCard — static presentational replica of the canvas DesignerNodeShell.
 *
 * This is a landing-page-only, dependency-free rebuild of the real designer
 * node card (CharacterDesignerNode / LocationDesignerNode). It faithfully
 * mirrors the product chrome — agent-colored border, floating type label,
 * render area, role label, and description — without pulling in the canvas'
 * YDoc / ReactFlow / API machinery. Agent colors match
 * constants/agentColors.ts exactly.
 */

interface DesignerNodeCardProps {
  /** Accent color (agent identity). Character = #EAB308, Location = #22C55E. */
  accent: string
  /** Floating type label above the card, e.g. "Character" / "Location". */
  typeLabel: string
  /** Node title, e.g. "Kenji Tanaka" / "Chef Hiroki's Ramen". */
  title: string
  /** Role / sub-label shown in accent color. */
  role: string
  /** Description body copy. */
  description: string
  /** Rendered artwork inside the node's image area. */
  imageSrc: string
  /** Image aspect ratio class — characters are 16:9, locations 4:3-ish. */
  aspect?: string
  className?: string
}

// Small type glyph in the floating label — a generic node dot.
function TypeGlyph({ color }: { color: string }) {
  return (
    <span
      className="inline-block w-2 h-2 rounded-full"
      style={{ background: color }}
    />
  )
}

export default function DesignerNodeCard({
  accent,
  typeLabel,
  title,
  role,
  description,
  imageSrc,
  aspect = 'aspect-video',
  className = '',
}: DesignerNodeCardProps) {
  return (
    <div className={`w-[400px] ${className}`}>
      {/* Floating node label */}
      <div className="flex items-center gap-1.5 mb-2 pl-1">
        <TypeGlyph color={accent} />
        <span className="font-[Fustat] font-semibold text-base text-white/90">
          {title}
        </span>
        <span className="font-[Fustat] text-base text-white/40">· {typeLabel}</span>
      </div>

      {/* Card */}
      <div
        className="rounded-2xl bg-[#141416] overflow-hidden"
        style={{
          border: `1px solid ${accent}`,
          boxShadow: `0 0 20px 2px ${accent}22, 0 24px 48px -12px rgba(0,0,0,0.6)`,
        }}
      >
        {/* Render area */}
        <div className={`relative w-full ${aspect} overflow-hidden bg-black`}>
          <img
            src={imageSrc}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="pt-3 px-5 pb-4">
          <span
            className="font-[Fustat] text-sm font-medium"
            style={{ color: accent }}
          >
            {role}
          </span>
          <p className="mt-1.5 font-[Fustat] text-base text-white/60 leading-relaxed line-clamp-3">
            {description}
          </p>
        </div>
      </div>
    </div>
  )
}
